import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { OVERVIEW_SEDIMENT_MOLD_PARAMETER_ID } from '@ivario-arc/shared/src/sampleResults/mold.ts';
import { MoldConcentration } from '@ivario-arc/shared/src/enums.ts';
import { SampleDetailsDto } from '@src/types/types.ts';
import { ParameterDetailPopup } from '@components/atoms/ParameterDetailPopup.tsx';
import { StyledMarkdown } from '@components/atoms/StyledMarkdown.tsx';
import { ActiveLink } from '@components/atoms/ActiveLink.tsx';
import { RegulationsUpselling } from '@components/sample-details/RegulationsUpselling.tsx';
import { Card } from '@components/atoms/Card.tsx';
import StatusMessage from '@components/sample-details/ParametersStatusMessage.tsx';
import {
  getMoldConcentrationFromRegulation,
  getNumericValue,
} from '@utils/parameters.ts';
import { SedimentMoldParameter } from '@components/sample-details/mold/SedimentMoldParameter.tsx';
import { ErrorContent } from '@components/atoms/ErrorContent.tsx';
import { ParameterStatusType } from '@components/atoms/ParameterStatusType.ts';
import { useConfigurationContext } from '@hooks/useConfiguration.tsx';

export const SedimentMoldResults = ({
  sample,
}: {
  sample: SampleDetailsDto;
}) => {
  const { t } = useTranslation('component', {
    keyPrefix: 'parameterList',
  });
  const { t: tError } = useTranslation('component', {
    keyPrefix: 'parameterList.moldAnalysisError',
  });
  const [showDetail, setShowDetail] = useState(false);
  const overviewParameter = sample.parameters.find(
    (parameter) => parameter.pmId === OVERVIEW_SEDIMENT_MOLD_PARAMETER_ID,
  );
  const { configuration } = useConfigurationContext();
  if (!overviewParameter) {
    return (
      <ErrorContent
        fullScreen={false}
        backToProfile={tError('backToProfile')}
        title={tError('title')}
        description={t('errors.noMoldDataAvailable')}
      />
    );
  }

  if (overviewParameter.value === 'N/A') {
    return (
      <StatusMessage
        type={ParameterStatusType.UNSET}
        title={t(`moldValues.NA`)}
        description={
          configuration?.defaultSedimentMoldNotAnalyzableClarification ?? ''
        }
        onInfoTap={() => {
          setShowDetail(true);
        }}
        labels={[]}
      />
    );
  }

  const overviewNumericValue = getNumericValue(overviewParameter);
  const otherParameters = sample.parameters
    .filter(
      (parameter) =>
        parameter.pmId !== OVERVIEW_SEDIMENT_MOLD_PARAMETER_ID &&
        parameter.format === 'numeric',
    )
    .sort(
      (a, b) =>
        getNumericValue(b) - getNumericValue(a) || a.name.localeCompare(b.name),
    );

  const labels = [
    {
      concentration: MoldConcentration.NO_MOLD,
      isActive: overviewNumericValue === 0,
      regulation: undefined,
      label: undefined,
    },
    ...(overviewParameter.regulations.map((regulation) => ({
      concentration: getMoldConcentrationFromRegulation(regulation),

      isActive:
        (!regulation.lowerLimit ||
          overviewNumericValue >= regulation.lowerLimit) &&
        (!regulation.limit || overviewNumericValue < regulation.limit),
      regulation: regulation,
      label: regulation.name?.includes('|') ? undefined : regulation.name,
    })) ?? []),
  ];

  const activeLabel = labels.find((l) => l.isActive) ?? labels[0];
  return (
    <div className="space-y-6">
      <StatusMessage
        type={
          activeLabel.concentration === MoldConcentration.MEDIUM_MOLD
            ? ParameterStatusType.WARNING
            : activeLabel.concentration === MoldConcentration.HIGH_MOLD
              ? ParameterStatusType.ERROR
              : ParameterStatusType.SUCCESS
        }
        title={t(`moldValues.${activeLabel.concentration}`)}
        percentage={overviewParameter.value}
        percentageDescription={t('CFU')}
        description={t(`moldShortInfo.${activeLabel.concentration}`)}
        onInfoTap={() => {
          setShowDetail(true);
        }}
        labels={labels.map((l) => ({
          isActive: l.isActive,
          text: l.label ?? t(`moldValues.${l.concentration}`),
        }))}
      />
      <StyledMarkdown markdown={activeLabel.regulation?.explanation} />
      <ActiveLink onTap={() => setShowDetail(true)} isActive={true}>
        <span className="text-ivarioBlue">{t('moreInfoOnParam')}</span>
      </ActiveLink>
      {(activeLabel.regulation?.upselling?.length ?? 0) > 0 && (
        <Card>
          <RegulationsUpselling
            upsellingItems={activeLabel.regulation?.upselling}
          />
        </Card>
      )}
      {showDetail && overviewParameter && (
        <ParameterDetailPopup
          parameter={overviewParameter}
          onClose={() => setShowDetail(false)}
        />
      )}
      {otherParameters?.map((p) => (
        <SedimentMoldParameter key={p.id} parameter={p} />
      ))}
    </div>
  );
};
